import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Paper, Typography, TextField, Button, List, ListItem, 
  ListItemButton, ListItemText, ListItemIcon, Checkbox, Divider, Tabs, Tab, Table, 
  TableBody, TableCell, TableHead, TableRow, Tooltip, Dialog, 
  DialogTitle, DialogContent, DialogActions, Alert, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';

import api from '../../api';

const HoldSalaryManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });

  // Tab 1: Manual Hold State
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [holdReason, setHoldReason] = useState('');

  // Tab 2: Report State
  const [reportData, setReportData] = useState([]);
  const [releaseModal, setReleaseModal] = useState({ open: false, empId: '', reason: '' });

  const adminName = localStorage.getItem('emp_name') || 'Admin User';

  const fetchData = async () => {
    try {
      const [eligibleRes, reportRes] = await Promise.all([
        api.get('/payroll/hold-salary/eligible-employees'),
        api.get('/payroll/hold-salary/report')
      ]);
      setEligibleEmployees(eligibleRes.data || []);
      setReportData(reportRes.data || []);
    } catch (err) {
      showNotif('Failed to load data from server.', 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  // --- Manual Hold Logic ---
  const filteredEmployees = useMemo(() => {
    return eligibleEmployees.filter(emp => 
      emp.emp_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      emp.emp_id.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [eligibleEmployees, searchQuery]);

  const handleToggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const renderedEmployeeList = useMemo(() => {
    if (filteredEmployees.length === 0) {
      return <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>No eligible employees found.</Typography>;
    }
    return filteredEmployees.map((emp) => (
      <React.Fragment key={emp.emp_id}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleToggle(emp.emp_id)}>
            <ListItemIcon><Checkbox edge="start" checked={selectedIds.includes(emp.emp_id)} disableRipple /></ListItemIcon>
            <ListItemText primary={emp.emp_name} secondary={`ID: ${emp.emp_id}`} />
          </ListItemButton>
        </ListItem>
        <Divider />
      </React.Fragment>
    ));
  }, [filteredEmployees, selectedIds]);

  const selectedEmployeesData = useMemo(() => {
    return selectedIds.map(id => {
      const e = eligibleEmployees.find(emp => emp.emp_id === id);
      return e ? `${e.emp_name} (${id})` : id;
    });
  }, [selectedIds, eligibleEmployees]);

  const handleApplyHold = async () => {
    if (selectedIds.length === 0 || !holdReason.trim()) return;
    try {
      await api.post('/payroll/hold-salary/manual-hold', {
        emp_ids: selectedIds,
        reason: holdReason,
        user_name: adminName
      });
      showNotif(`Successfully placed ${selectedIds.length} employee(s) on Hold.`);
      setSelectedIds([]);
      setHoldReason('');
      fetchData();
      setActiveTab(1);
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Failed to apply hold.', 'error');
    }
  };

  // --- Release Hold Logic ---
  const handleRelease = async () => {
    if (!releaseModal.reason.trim()) return;
    try {
      await api.post(`/payroll/hold-salary/release/${releaseModal.empId}`, {
        reason: releaseModal.reason,
        user_name: adminName
      });
      showNotif(`Successfully released Hold Salary for ${releaseModal.empId}.`);
      setReleaseModal({ open: false, empId: '', reason: '' });
      fetchData();
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Failed to release hold.', 'error');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
          <Tab label="Apply Manual Hold" icon={<LockIcon fontSize="small" />} iconPosition="start" sx={{ fontWeight: 'bold' }} />
          <Tab label="Hold Salary Report & Release" icon={<TableChartIcon fontSize="small" />} iconPosition="start" sx={{ fontWeight: 'bold' }} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            // Switched from Grid to a strict Flex container to force the side-by-side layout
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, width: '100%', alignItems: 'stretch' }}>
              
              {/* Left Panel: Strictly locked to 40% width */}
              <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 38%' }, minWidth: 0, width: '100%' }}>
                <Paper variant="outlined" sx={{ borderRadius: 2, display: 'flex', flexDirection: 'column', height: 480, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Select Eligible Employees</Typography>
                    <TextField 
                      fullWidth size="small" placeholder="Search by ID or Name..." 
                      value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{ startAdornment: <SearchIcon color="action" fontSize="small" sx={{ mr: 1 }} /> }}
                    />
                  </Box>
                  <List sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', p: 0 }}>
                    {renderedEmployeeList}
                  </List>
                </Paper>
              </Box>

              {/* Right Panel: Strictly locked to 62% width */}
              <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 62%' }, minWidth: 0, width: '100%' }}>
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, height: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                    Selected for Hold ({selectedIds.length})
                  </Typography>
                  
                  {/* Container for Chips */}
                  <Box sx={{ 
                    flexGrow: 1, 
                    overflowY: 'auto', 
                    overflowX: 'hidden', 
                    width: '100%', 
                    boxSizing: 'border-box',
                    mb: 2, 
                    p: 1.5, 
                    bgcolor: '#fafafa', 
                    borderRadius: 1, 
                    border: '1px dashed #ccc' 
                  }}>
                    {selectedEmployeesData.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 5 }}>No employees selected.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedEmployeesData.map(label => (
                          <Chip 
                            key={label} 
                            label={label} 
                            size="small" 
                            color="error" 
                            variant="outlined" 
                            sx={{ 
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  <TextField 
                    fullWidth multiline rows={3} required
                    label="Mandatory Hold Reason" 
                    placeholder="Enter explicit reason for holding salary..."
                    value={holdReason} onChange={(e) => setHoldReason(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <Button 
                    variant="contained" color="error" fullWidth startIcon={<LockIcon />}
                    onClick={handleApplyHold} disabled={selectedIds.length === 0 || !holdReason.trim()}
                    sx={{ py: 1.5 }}
                  >
                    Apply Hold Salary
                  </Button>
                </Paper>
              </Box>

            </Box>
          )}

          {/* TAB 2: REPORT & RELEASE */}
          {activeTab === 1 && (
            <Box>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Hold Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Emp ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Employee Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.hold_date}</TableCell>
                      <TableCell>{row.emp_id}</TableCell>
                      <TableCell>{row.emp_name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={row.source} size="small" color={row.source === 'Auto' ? 'warning' : 'default'} />
                          <Tooltip title={row.reason} arrow>
                            <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.current_status} size="small" color={row.current_status === 'Hold Salary' ? 'error' : 'success'} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        {row.current_status === 'Hold Salary' && (
                          <Button size="small" variant="outlined" color="success" startIcon={<LockOpenIcon />} onClick={() => setReleaseModal({ open: true, empId: row.emp_id, reason: '' })}>
                            Release
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {reportData.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center">No hold records found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Release Dialog */}
      <Dialog open={releaseModal.open} onClose={() => setReleaseModal({ ...releaseModal, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'success.main' }}>Release Hold Salary</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are releasing <strong>{releaseModal.empId}</strong> from Hold Salary. Please provide a mandatory release reason.
          </Typography>
          <TextField 
            fullWidth autoFocus required multiline rows={3}
            label="Release Reason" 
            placeholder="e.g., Employee resumed work, investigation cleared..."
            value={releaseModal.reason} 
            onChange={(e) => setReleaseModal({ ...releaseModal, reason: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReleaseModal({ ...releaseModal, open: false })} color="inherit">Cancel</Button>
          <Button onClick={handleRelease} variant="contained" color="success" disabled={!releaseModal.reason.trim()}>
            Confirm Release
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HoldSalaryManagement;